import React, { useRef } from 'react';
import { Box, InputBase, InputLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface CodeInputProps {
  id: string;
  length?: number;
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  disabled?: boolean;
}

/**
 * Whether `value` holds a full code. Empty boxes before the last digit are
 * kept as spaces, so the length alone doesn't say the code is complete.
 */
export function isCompleteCode(value: string, length = 6): boolean {
  return value.length === length && /^\d+$/.test(value);
}

/**
 * One box per digit, ported from Dynamica. Spacing between the label, the
 * boxes and the error uses margins rather than `gap`: LastPass injects a 0x0
 * sibling div next to the inputs, and with `gap` it counts as a flex item and
 * adds an extra gap (its inline styles are `!important`, so CSS can't hide it).
 */
export function CodeInput({
  id,
  length = 6,
  value,
  onChange,
  error,
  disabled,
}: CodeInputProps) {
  const { t } = useTranslation();
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length }, (_, index) =>
    (value[index] ?? '').trim()
  );
  const errorId = `${id}-error`;

  const focusAt = (index: number) => {
    inputRefs.current[Math.max(0, Math.min(index, length - 1))]?.focus();
  };

  // An empty box becomes a space, so clearing one doesn't shift the digits
  // after it into its place.
  const commit = (nextDigits: string[]) => {
    onChange(
      nextDigits
        .map((digit) => digit || ' ')
        .join('')
        .trimEnd()
    );
  };

  const setDigitAt = (index: number, digit: string) => {
    const nextDigits = [...digits];
    nextDigits[index] = digit;
    commit(nextDigits);
  };

  // Writes `typed` into the boxes from `index` on, as far as they go.
  const fillFrom = (index: number, typed: string) => {
    const nextDigits = [...digits];
    const count = Math.min(typed.length, length - index);
    for (let i = 0; i < count; i++) {
      nextDigits[index + i] = typed[i];
    }
    commit(nextDigits);
    focusAt(index + count);
  };

  const handleChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const typed = e.target.value.replace(/[^0-9]/g, '');
    // Autofill (one-time-code) can drop the whole code into one box.
    if (typed.length >= length) {
      fillFrom(0, typed);
      return;
    }
    // Typing into a box that already has a digit: keep only the new one.
    if (typed.length === 2 && digits[index]) {
      const old = typed.indexOf(digits[index]);
      const digit = old === -1 ? typed.slice(-1) : typed[1 - old];
      setDigitAt(index, digit);
      focusAt(index + 1);
      return;
    }
    if (typed.length > 1) {
      fillFrom(index, typed);
      return;
    }
    setDigitAt(index, typed);
    if (typed && index < length - 1) {
      focusAt(index + 1);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      e.preventDefault();
      setDigitAt(index - 1, '');
      focusAt(index - 1);
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData
      .getData('text')
      .replace(/[^0-9]/g, '')
      .slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    fillFrom(0, pasted);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 16 }}>
      <InputLabel htmlFor={`${id}-0`} sx={{ mb: 6, color: 'text.primary' }}>
        {t('auth.welcome.code.label')}
      </InputLabel>
      <Box
        role="group"
        aria-label={t('auth.welcome.code.groupLabel') as string}
        sx={{ display: 'flex', justifyContent: 'space-between' }}
      >
        {digits.map((digit, index) => (
          <InputBase
            key={index}
            inputRef={(el: HTMLInputElement | null) => {
              inputRefs.current[index] = el;
            }}
            id={`${id}-${index}`}
            value={digit}
            disabled={disabled}
            onChange={(e) =>
              handleChange(index, e as React.ChangeEvent<HTMLInputElement>)
            }
            onKeyDown={(e) =>
              handleKeyDown(index, e as React.KeyboardEvent<HTMLInputElement>)
            }
            onPaste={(e) =>
              handlePaste(e as React.ClipboardEvent<HTMLInputElement>)
            }
            onFocus={(e) => (e.target as HTMLInputElement).select()}
            autoFocus={index === 0}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              autoComplete: index === 0 ? 'one-time-code' : 'off',
              'aria-label': t('auth.welcome.code.digitLabel', {
                n: index + 1,
                length,
              }) as string,
              'aria-invalid': error ? true : undefined,
              'aria-describedby': error ? errorId : undefined,
            }}
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 48, sm: 56 },
              ml: index === 0 ? 0 : { xs: 4, sm: 8 },
              border: '1px solid',
              borderColor: error
                ? 'error.main'
                : 'var(--color-mid-greyish-blue)',
              borderRadius: '8px',
              fontSize: 22,
              fontWeight: 600,
              '&.Mui-focused': {
                borderColor: 'primary.main',
                boxShadow: (theme) =>
                  `0 0 0 2px ${theme.palette.primary.main}40`,
              },
              '& input': { textAlign: 'center', p: 0 },
            }}
          />
        ))}
      </Box>
      <Typography
        id={errorId}
        role="alert"
        aria-atomic="true"
        variant="body2"
        color="error"
        sx={{ mt: error ? 8 : 0, minHeight: 0 }}
      >
        {error}
      </Typography>
    </Box>
  );
}
